import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { slides } from "@/mocks/slides";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 5200);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [paused]);

  const goTo = (index: number) => setActive((index + slides.length) % slides.length);

  return (
    <section
      aria-label="焦点内容推荐"
      className="relative w-full h-[320px] sm:h-[380px] lg:h-[440px] rounded-lg overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, index) => (
        <Link
          key={slide.id}
          to={`/detail/${slide.id}`}
          aria-hidden={index !== active}
          tabIndex={index === active ? 0 : -1}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title}焦点剧照`}
            title={`${slide.title} 奇优影院推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/55 to-primary-950/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 via-primary-950/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-7 lg:p-9">
            <p className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-[11px] md:text-xs font-label mb-3">
              <i className="ri-fire-line text-sm w-4 h-4 flex items-center justify-center" />
              {slide.kicker}
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-background-50 font-heading tracking-tight">
              {slide.title}
            </h2>
            <p className="mt-2 text-sm md:text-base text-background-200/90 max-w-xl leading-relaxed">
              {slide.subtitle}
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-50 text-primary-800 text-sm font-medium">
              查看详情
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </span>
          </div>
        </Link>
      ))}

      <button
        type="button"
        onClick={() => goTo(active - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-primary-950/45 text-background-50 flex items-center justify-center hover:bg-accent-500 transition-colors cursor-pointer backdrop-blur-sm"
      >
        <i className="ri-arrow-left-s-line text-xl w-5 h-5 flex items-center justify-center" />
      </button>
      <button
        type="button"
        onClick={() => goTo(active + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-primary-950/45 text-background-50 flex items-center justify-center hover:bg-accent-500 transition-colors cursor-pointer backdrop-blur-sm"
      >
        <i className="ri-arrow-right-s-line text-xl w-5 h-5 flex items-center justify-center" />
      </button>

      <div className="absolute bottom-4 right-4 md:right-7 flex items-center gap-1.5">
        {slides.map((slide, index) => (
          <button
            key={`dot-${slide.id}`}
            type="button"
            aria-label={`切换到${slide.title}`}
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? "w-6 bg-secondary-400" : "w-1.5 bg-background-50/50 hover:bg-background-50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}